import { useTimeEntryFormStore } from '@/services/useTimeEntryFormStore';

/**
 * SIMPLE Time Entry Panel ↔ Modal Persistence
 * 
 * Clean, bug-free approach:
 * 1. When user clicks expand/minimize → save current form state
 * 2. When target component loads → restore saved state if it exists  
 * 3. After successful save → clear the saved state
 * 
 * NO auto-save, NO complex timing, NO special flags
 */
export function useTimeEntryToggle() {
  const timeEntryFormStore = useTimeEntryFormStore();

  /**
   * Create session key for state storage
   * NOTE: Duplicates are treated as NEW entries since they create fresh entries
   */
  const getSessionKey = (editEntryState, combinedParams) => {
    // Duplicates should be treated as new entries (they create new entries, not edit existing ones)
    if (combinedParams.duplicateEntryId) {
      return 'new-entry-session';
    } else if (combinedParams.entryId) {
      return `entry-${combinedParams.entryId}`;
    } else {
      return 'new-entry-session';
    }
  };

  /**
   * Panel → Modal: Save state and transition
   */
  const handleExpand = (editEntryState, combinedParams, getFormState, emit) => {
    const sessionKey = getSessionKey(editEntryState, combinedParams);
    const currentFormState = getFormState();

    // Use the correct store API
    timeEntryFormStore.saveFormState(currentFormState, {
      type: 'expand',
      sourceComponent: 'panel',
      sessionKey: sessionKey,
      entryType: combinedParams.entryId ? 'EXISTING ENTRY' : 'NEW ENTRY',
      entryId: combinedParams.entryId
    });
    emit('expand');
    return true;
  };

  /**
   * Modal → Panel: Save state and transition
   */
  const handleMinimize = (editEntryState, combinedParams, getFormState, emit) => {
    const sessionKey = getSessionKey(editEntryState, combinedParams);
    const currentFormState = getFormState();

    // Use the correct store API
    timeEntryFormStore.saveFormState(currentFormState, {
      type: 'minimize',
      sourceComponent: 'modal',
      sessionKey: sessionKey,
      entryType: combinedParams.entryId ? 'EXISTING ENTRY' : 'NEW ENTRY',
      entryId: combinedParams.entryId
    });
    emit('minimize');
    return true;
  };

  /**
   * Initialize and restore saved state (call in component setup)
   */
  const initializePersistence = async (editEntryState, combinedParams, setFormState) => {
    const sessionKey = getSessionKey(editEntryState, combinedParams);
    const savedState = timeEntryFormStore.getFormState();
    if (savedState) {
      if (setFormState) {
        await setFormState(savedState);
      }
    }
    return !!savedState;
  };

  /**
   * Clear saved state (call after successful save)
   */
  const handlePostSaveClearance = (editEntryState, combinedParams) => {
    const sessionKey = getSessionKey(editEntryState, combinedParams);
    timeEntryFormStore.clearFormState();
    return true;
  };

  /**
   * Close handler (clear state when user cancels)
   */
  const handleClose = (editEntryState, combinedParams, getFormState, emit) => {
    handlePostSaveClearance(editEntryState, combinedParams);
    emit('close');
    return true;
  };
  return {
    initializePersistence,
    handleExpand,
    handleMinimize,
    handleClose,
    handlePostSaveClearance
  };
}